import { parseModuleValue } from '../registry';

export type LabelContent = { type: 'string'; value: string } | { type: 'module'; value: any };

export function parseLabel(value: string): LabelContent {
  const result = parseModuleValue(value);
  if (result.type === 'module') {
    return { type: 'module', value: result.value };
  }
  return { type: 'string', value: result.value as string };
}

export interface DotGraph {
  type: 'digraph' | 'graph';
  strict: boolean;
  id?: string;
  statements: DotStatement[];
  attributes: GraphAttributes;
}

export type DotStatement =
  | NodeStatement
  | EdgeStatement
  | Subgraph
  | AttributeStatement
  | Assignment;

export interface NodeStatement {
  type: 'node';
  nodeId: string;
  attributes: NodeAttributes;
}

export interface EdgeStatement {
  type: 'edge';
  edges: Edge[];
}

export interface Edge {
  from: string;
  to: string;
  attributes: EdgeAttributes;
}

export interface Subgraph {
  type: 'subgraph';
  id?: string;
  statements: DotStatement[];
  attributes: GraphAttributes;
}

export interface AttributeStatement {
  type: 'attr';
  target: 'graph' | 'node' | 'edge';
  attributes: Record<string, string>;
}

export interface Assignment {
  type: 'assignment';
  name: string;
  value: string;
}

export interface GraphAttributes {
  rankdir?: 'TB' | 'BT' | 'LR' | 'RL';
  ranksep?: number;
  nodesep?: number;
  margin?: number;
  bgcolor?: string;
  label?: string | LabelContent;
  fontname?: string;
  fontsize?: number;
  style?: string;
  color?: string;
  rank?: 'same' | 'min' | 'max' | 'source' | 'sink';
}

export interface NodeAttributes {
  label?: string | LabelContent;
  shape?: NodeShape;
  rank?: 'same' | 'min' | 'max' | 'source' | 'sink';
  style?: string;
  fillcolor?: string;
  color?: string;
  fontname?: string;
  fontsize?: number;
  fontcolor?: string;
  width?: number;
  height?: number;
  margin?: number;
}

export interface EdgeAttributes {
  label?: string | LabelContent;
  style?: 'solid' | 'dashed' | 'dotted' | 'bold';
  color?: string;
  penwidth?: number;
  arrowsize?: number;
  arrowhead?: ArrowType;
  weight?: number;
  constraint?: string;
}

export type NodeShape =
  | 'box'
  | 'ellipse'
  | 'circle'
  | 'diamond'
  | 'plaintext'
  | 'Mdiamond'
  | 'Msquare'
  | 'round';
export type ArrowType =
  | 'normal'
  | 'none'
  | 'box'
  | 'crow'
  | 'diamond'
  | 'dot'
  | 'inv'
  | 'tee'
  | 'vee';

export interface GraphLayout {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  clusters: LayoutCluster[];
  boundingBox: { width: number; height: number; minX: number; minY: number };
  rankDirection: 'TB' | 'BT' | 'LR' | 'RL';
}

export interface LayoutNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  shape: NodeShape;
  label: LabelContent;
  style: NodeStyle;
}

export interface NodeStyle {
  fill?: string;
  stroke?: string;
  font?: string;
  fontcolor?: string;
  fontsize?: number;
}

export interface LayoutEdge {
  from: string;
  to: string;
  points: { x: number; y: number }[];
  style: EdgeStyle;
}

export interface EdgeStyle {
  stroke?: string;
  strokeWidth?: number;
  strokeDash?: number[];
  arrowhead?: ArrowType;
}

export interface LayoutCluster {
  id: string;
  nodeIds: string[];
  rect: { x: number; y: number; width: number; height: number };
  label?: string;
  style: ClusterStyle;
}

export interface ClusterStyle {
  fill?: string;
  stroke?: string;
}
